import { useEffect, useState } from 'react';
import { logo } from '@/assets/ui';
import { bgMobile } from '@/assets/backgrounds';
import { useAudioContext } from '@/contexts/AudioContext';

interface PreloaderProps {
  error?: string | null;
  onRetry?: () => void;
}

export default function Preloader({ error, onRetry }: PreloaderProps) {
  const [progress, setProgress] = useState(0);
  const { playMusic } = useAudioContext();
  
  // Запускаем музыку лоадера
  useEffect(() => {
    playMusic('loader');
  }, [playMusic]);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(p => {
        if (p >= 92) {
          clearInterval(interval);
          return 92;
        }
        return p + Math.random() * 15;
      });
    }, 100);
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="w-full h-full flex flex-col items-center justify-center relative overflow-hidden">
      {/* Фоновое изображение */}
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${bgMobile})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
        }}
      />
      
      {/* Контент */}
      <div className="relative z-10 flex flex-col items-center gap-8">
        {/* Логотип */}
        <img 
          src={logo} 
          alt="Roll Towers" 
          className="w-64 h-auto"
          draggable={false}
        />
        
        {/* Анимированные палочки */}
        <div className="relative w-24 h-32">
          <div 
            className="absolute w-2 h-24 bg-gradient-to-b from-amber-200 to-amber-600 rounded-full origin-bottom"
            style={{
              left: '30%',
              animation: 'chopstick-left 1s ease-in-out infinite',
              transform: 'rotate(-15deg)',
            }}
          />
          <div 
            className="absolute w-2 h-24 bg-gradient-to-b from-amber-200 to-amber-600 rounded-full origin-bottom"
            style={{
              right: '30%',
              animation: 'chopstick-right 1s ease-in-out infinite',
              transform: 'rotate(15deg)',
            }}
          />
          {/* Ролл между палочками */}
          <div 
            className="absolute bottom-0 left-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-gradient-to-br from-rice to-gray-200 border-4 border-nori shadow-lg"
            style={{
              animation: 'roll-bounce 1s ease-in-out infinite',
            }}
          >
            <div className="absolute inset-2 rounded-full bg-gradient-to-br from-salmon to-tuna" />
          </div>
        </div>
        
        {error ? (
          <div className="flex w-64 flex-col items-center gap-3 text-center">
            <div
              className="text-xl text-[#F8DDCA]"
              style={{ fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 700 }}
            >
              {error}
            </div>
            <button
              type="button"
              onClick={onRetry}
              className="h-10 min-w-32 bg-[#DC3F48] px-6 text-xl text-[#F8DDCA] shadow-[inset_0_-3px_0_#AA2930]"
              style={{ fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 700 }}
            >
              RETRY
            </button>
          </div>
        ) : (
          <div className="w-48 h-2 bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-gold to-sakura rounded-full transition-all duration-200"
              style={{ width: `${Math.min(progress, 92)}%` }}
            />
          </div>
        )}
      </div>
      
      {/* CSS анимации */}
      <style>{`
        @keyframes chopstick-left {
          0%, 100% { transform: rotate(-15deg); }
          50% { transform: rotate(-5deg); }
        }
        @keyframes chopstick-right {
          0%, 100% { transform: rotate(15deg); }
          50% { transform: rotate(5deg); }
        }
        @keyframes roll-bounce {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50% { transform: translateX(-50%) translateY(-10px); }
        }
      `}</style>
    </div>
  );
}

